import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { EmployeeImage } from '@/components/EmployeeImage';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Search,
  Filter,
  Briefcase,
  Calendar,
  Users,
  DollarSign,
  Clock,
  AlertCircle,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  Building2,
  Mail,
  Phone,
  Target,
  Package,
  Info,
  ArrowUpDown,
  Eye,
  LayoutGrid,
  LayoutList,
  Download,
  X,
  Plus,
  FilterX,
  Loader2,
  FolderOpen,
  SearchX,
  CalendarClock,
} from 'lucide-react';
import { mockProjects } from '@/data/projectsData';
import type { ProjectDetails, ProjectStatus, ProjectPriority } from '@/types/project';
import { format } from 'date-fns';
import { ActiveProjectsGrid } from '@/components/ActiveProjectsGrid';
import { EnhancedProjectCard } from '@/components/projects/EnhancedProjectCard';
import { cn } from '@/lib/utils';
import { useDataSource } from '@/contexts/DataSourceContext';
import { projectsApi } from '@/services/projectsApi';
import { toast } from 'sonner';
import { debounce } from '@/utils/debounce';

// Skeleton Loading Component
const ProjectCardSkeleton: React.FC = () => {
  return (
    <Card className="animate-pulse">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-2 flex-1">
            <div className="flex items-center gap-2">
              <div className="h-5 w-16 bg-gray-300 dark:bg-gray-700 rounded"></div>
              <div className="h-5 w-16 bg-gray-300 dark:bg-gray-700 rounded"></div>
            </div>
            <div className="h-6 w-3/4 bg-gray-300 dark:bg-gray-700 rounded"></div>
            <div className="h-4 w-1/2 bg-gray-300 dark:bg-gray-700 rounded"></div>
          </div>
          <div className="h-6 w-20 bg-gray-300 dark:bg-gray-700 rounded"></div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="h-10 bg-gray-300 dark:bg-gray-700 rounded"></div>
        <div className="space-y-2">
          <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded"></div>
          <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded"></div>
        </div>
        <div className="h-8 bg-gray-300 dark:bg-gray-700 rounded"></div>
      </CardContent>
    </Card>
  );
};

const SkeletonGrid: React.FC<{ count?: number }> = ({ count = 6 }) => {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: count }).map((_, index) => (
        <ProjectCardSkeleton key={index} />
      ))}
    </div>
  );
};

// Enhanced Status Badge with Icons
const StatusBadge: React.FC<{ status: ProjectStatus }> = ({ status }) => {
  const getStatusConfig = (status: ProjectStatus) => {
    switch (status) {
      case 'active':
        return {
          color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-green-500/20',
          icon: CheckCircle2,
          label: 'Active'
        };
      case 'at-risk':
        return {
          color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-amber-500/20',
          icon: AlertCircle,
          label: 'At Risk'
        };
      case 'on-hold':
        return {
          color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400 border-gray-500/20',
          icon: Clock,
          label: 'On Hold'
        };
      default:
        return {
          color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-blue-500/20',
          icon: Briefcase,
          label: status
        };
    }
  };

  const config = getStatusConfig(status);
  const Icon = config.icon;

  return (
    <Badge variant="outline" className={cn("inline-flex items-center gap-1", config.color)}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

// Enhanced Priority Badge with Icons
const PriorityBadge: React.FC<{ priority: ProjectPriority }> = ({ priority }) => {
  const getPriorityConfig = (priority: ProjectPriority) => {
    switch (priority) {
      case 'high':
        return {
          color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-red-500/20',
          icon: TrendingUp,
          label: 'High'
        };
      case 'medium':
        return {
          color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-blue-500/20',
          icon: ArrowUpDown,
          label: 'Medium'
        };
      case 'low':
        return {
          color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400 border-gray-500/20',
          icon: ArrowUpDown,
          label: 'Low'
        };
    }
  };

  const config = getPriorityConfig(priority);
  const Icon = config.icon;

  return (
    <Badge variant="outline" className={cn("inline-flex items-center gap-1", config.color)}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

export const ActiveProjectsPage: React.FC = () => {
  const { getScreenMode } = useDataSource();
  const screenMode = getScreenMode('projects-active');
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');
  const [selectedProject, setSelectedProject] = useState<ProjectDetails | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showProjectManagementModal, setShowProjectManagementModal] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'cards'>('cards');
  
  const [apiProjects, setApiProjects] = useState<ProjectDetails[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const projects = screenMode === 'live' ? apiProjects : mockProjects;
  
  useEffect(() => {
    if (screenMode === 'live') {
      setIsLoading(true);
      projectsApi.getAllProjects()
        .then(projects => {
          setApiProjects(projects);
          toast.success('Projects loaded successfully', {
            description: `Loaded ${projects.length} projects from the database`,
          });
        })
        .catch(error => {
          console.error('Failed to fetch projects:', error);
          toast.error('Failed to load projects', {
            description: 'Could not connect to the API. Please check your connection and try again.',
          });
          setApiProjects([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [screenMode]);

  // Debounced search handler
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      // Search is handled in the filteredProjects memo
    }, 300),
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Handler for metric card clicks - synchronizes with filter dropdowns
  const handleMetricCardClick = (metric: 'total' | 'at-risk' | 'high-priority') => {
    // Check if this metric is already active
    const isActive = 
      (metric === 'total' && statusFilter === 'all' && priorityFilter === 'all') ||
      (metric === 'at-risk' && statusFilter === 'at-risk') ||
      (metric === 'high-priority' && priorityFilter === 'high');

    if (isActive) {
      // Clear all filters
      setStatusFilter('all');
      setPriorityFilter('all');
    } else {
      // Apply the selected metric filter
      if (metric === 'total') {
        setStatusFilter('all');
        setPriorityFilter('all');
      } else if (metric === 'at-risk') {
        setStatusFilter('at-risk');
        setPriorityFilter('all');
      } else if (metric === 'high-priority') {
        setStatusFilter('all');
        setPriorityFilter('high');
      }
    }
  };

  // Determine active metric based on current filters
  const getActiveMetric = (): 'total' | 'at-risk' | 'high-priority' | null => {
    if (statusFilter === 'at-risk' && priorityFilter === 'all') {
      return 'at-risk';
    } else if (statusFilter === 'all' && priorityFilter === 'high') {
      return 'high-priority';
    } else if (statusFilter === 'all' && priorityFilter === 'all') {
      return 'total';
    }
    return null;
  };

  const activeMetric = getActiveMetric();

  // Filter and sort projects
  const filteredProjects = useMemo(() => {
    let filtered = projects.filter(project => {
      // Search filter
      const matchesSearch = searchQuery === '' || 
        project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.clientName.toLowerCase().includes(searchQuery.toLowerCase());

      // Status filter
      const matchesStatus = statusFilter === 'all' || project.status === statusFilter;

      // Priority filter
      const matchesPriority = priorityFilter === 'all' || project.priority === priorityFilter;

      return matchesSearch && matchesStatus && matchesPriority;
    });

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'code':
          return a.code.localeCompare(b.code);
        case 'client':
          return a.clientName.localeCompare(b.clientName);
        case 'startDate':
          return a.timeline.startDate.getTime() - b.timeline.startDate.getTime();
        case 'endDate':
          return a.timeline.endDate.getTime() - b.timeline.endDate.getTime();
        default:
          return 0;
      }
    });

    return filtered;
  }, [projects, searchQuery, statusFilter, priorityFilter, sortBy]);

  const handleViewDetails = (project: ProjectDetails) => {
    setSelectedProject(project);
    setShowDetailsDialog(true);
  };

  const getStatusColor = (status: ProjectStatus) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20';
      case 'at-risk':
        return 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20';
      case 'on-hold':
        return 'bg-gray-500/10 text-gray-700 dark:text-gray-400 border-gray-500/20';
      default:
        return 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20';
    }
  };

  const getPriorityColor = (priority: ProjectPriority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20';
      case 'medium':
        return 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20';
      case 'low':
        return 'bg-gray-500/10 text-gray-700 dark:text-gray-400 border-gray-500/20';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Check if any filters are active
  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all' || priorityFilter !== 'all';

  // Clear all filters
  const handleClearAllFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPriorityFilter('all');
  };

  // Export functionality
  const handleExportProjects = () => {
    toast.success('Export started', {
      description: 'Your project list is being prepared for download.',
    });
    // TODO: Implement actual export logic
  };

  // Determine empty state type
  const getEmptyStateType = (): 'no-data' | 'no-search' | 'no-filters' => {
    if (projects.length === 0) return 'no-data';
    if (searchQuery !== '' && filteredProjects.length === 0) return 'no-search';
    if (hasActiveFilters && filteredProjects.length === 0) return 'no-filters';
    return 'no-data';
  };

  // Enhanced Empty State Component
  const EmptyState: React.FC = () => {
    const emptyType = getEmptyStateType();

    const emptyStates = {
      'no-data': {
        icon: FolderOpen,
        title: 'No projects yet',
        description: 'Get started by creating your first project to track time and manage your work.',
        action: (
          <Button className="mt-4" size="lg">
            <Plus className="h-4 w-4 mr-2" />
            Create Your First Project
          </Button>
        )
      },
      'no-search': {
        icon: SearchX,
        title: `No projects match "${searchQuery}"`,
        description: 'Try adjusting your search terms or check for typos.',
        action: (
          <Button variant="outline" className="mt-4" onClick={handleClearSearch}>
            <X className="h-4 w-4 mr-2" />
            Clear Search
          </Button>
        )
      },
      'no-filters': {
        icon: FilterX,
        title: 'No projects match your filters',
        description: 'Try adjusting your filter criteria to see more results.',
        action: (
          <Button variant="outline" className="mt-4" onClick={handleClearAllFilters}>
            <X className="h-4 w-4 mr-2" />
            Clear All Filters
          </Button>
        )
      }
    };

    const state = emptyStates[emptyType];
    const Icon = state.icon;

    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="rounded-full bg-muted p-6 mb-4">
          <Icon className="h-12 w-12 text-muted-foreground" />
        </div>
        <h3 className="text-xl font-semibold mb-2">{state.title}</h3>
        <p className="text-sm text-muted-foreground text-center max-w-md mb-4">
          {state.description}
        </p>
        {state.action}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Active Projects</h1>
        <p className="text-muted-foreground mt-2">
          View comprehensive project information to help you accurately log time entries
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Total Projects - Blue Accent */}
        <Card 
          className={cn(
            "relative overflow-hidden border-l-[3px] border-l-blue-500 bg-gradient-to-br from-blue-500/10 to-transparent dark:from-blue-500/5 cursor-pointer hover:shadow-lg transition-all",
            activeMetric === 'total' && "ring-2 ring-blue-500"
          )}
          onClick={() => handleMetricCardClick('total')}
          role="button"
          tabIndex={0}
          aria-label="Filter by all projects"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleMetricCardClick('total');
            }
          }}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
            <Briefcase className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-700 dark:text-blue-300">{projects.length}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {projects.filter(p => p.status === 'active').length} active
            </p>
          </CardContent>
        </Card>

        {/* Team Members - Green Accent (Non-clickable for now) */}
        <Card 
          className="relative overflow-hidden border-l-[3px] border-l-green-500 bg-gradient-to-br from-green-500/10 to-transparent dark:from-green-500/5"
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Team Members</CardTitle>
            <Users className="h-5 w-5 text-green-600 dark:text-green-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-700 dark:text-green-300">
              {projects.reduce((sum, p) => sum + p.teamSize, 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Across all projects
            </p>
          </CardContent>
        </Card>

        {/* At Risk - Amber Accent with Pulse Animation */}
        <Card 
          className={cn(
            "relative overflow-hidden border-l-[4px] border-l-amber-500 bg-gradient-to-br from-amber-500/20 to-transparent dark:from-amber-500/10 cursor-pointer hover:shadow-lg transition-all animate-pulse-subtle",
            activeMetric === 'at-risk' && "ring-2 ring-amber-500"
          )}
          onClick={() => handleMetricCardClick('at-risk')}
          role="button"
          tabIndex={0}
          aria-label="Filter by at-risk projects"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleMetricCardClick('at-risk');
            }
          }}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              At Risk
              <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">
                ACTION REQUIRED
              </Badge>
            </CardTitle>
            <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-amber-700 dark:text-amber-300">
              {projects.filter(p => p.status === 'at-risk').length}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Require immediate attention
            </p>
          </CardContent>
        </Card>

        {/* High Priority - Red Accent */}
        <Card 
          className={cn(
            "relative overflow-hidden border-l-[4px] border-l-red-500 bg-gradient-to-br from-red-500/20 to-transparent dark:from-red-500/10 cursor-pointer hover:shadow-lg transition-all",
            activeMetric === 'high-priority' && "ring-2 ring-red-500"
          )}
          onClick={() => handleMetricCardClick('high-priority')}
          role="button"
          tabIndex={0}
          aria-label="Filter by high priority projects"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleMetricCardClick('high-priority');
            }
          }}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">High Priority</CardTitle>
            <TrendingUp className="h-5 w-5 text-red-600 dark:text-red-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-700 dark:text-red-300">
              {projects.filter(p => p.priority === 'high').length}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {projects.length > 0 ? Math.round((projects.filter(p => p.priority === 'high').length / projects.length) * 100) : 0}% of portfolio
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Enhanced Filters and Search */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Search & Filter</CardTitle>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAllFilters}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4 mr-1" />
                Clear All
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            {/* Enhanced Search with Icon and Clear Button */}
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" 
                  aria-hidden="true" 
                />
                <Input
                  placeholder="Search projects, codes, or clients..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="pl-9 pr-9"
                  aria-label="Search projects"
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearSearch}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 hover:bg-transparent"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                  </Button>
                )}
              </div>
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger aria-label="Filter by status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="at-risk">At Risk</SelectItem>
                <SelectItem value="on-hold">On Hold</SelectItem>
              </SelectContent>
            </Select>

            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger aria-label="Filter by priority">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger aria-label="Sort projects">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Project Name</SelectItem>
                <SelectItem value="code">Project Code</SelectItem>
                <SelectItem value="client">Client Name</SelectItem>
                <SelectItem value="startDate">Start Date</SelectItem>
                <SelectItem value="endDate">End Date</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Active Filter Chips */}
          {hasActiveFilters && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Active filters:
              </span>
              {searchQuery && (
                <Badge variant="secondary" className="gap-1">
                  Search: {searchQuery}
                  <button
                    onClick={handleClearSearch}
                    className="ml-1 hover:bg-muted rounded-full"
                    aria-label="Remove search filter"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {statusFilter !== 'all' && (
                <Badge variant="secondary" className="gap-1">
                  Status: {statusFilter}
                  <button
                    onClick={() => setStatusFilter('all')}
                    className="ml-1 hover:bg-muted rounded-full"
                    aria-label="Remove status filter"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {priorityFilter !== 'all' && (
                <Badge variant="secondary" className="gap-1">
                  Priority: {priorityFilter}
                  <button
                    onClick={() => setPriorityFilter('all')}
                    className="ml-1 hover:bg-muted rounded-full"
                    aria-label="Remove priority filter"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
            </div>
          )}

          {hasActiveFilters && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Showing {filteredProjects.length} of {projects.length} projects
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Projects View with Toggle and Export */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Projects</CardTitle>
              <CardDescription>
                View projects in grid or card format
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {/* Export Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportProjects}
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                Export
              </Button>
              {/* View Mode Icons */}
              <div className="flex gap-1 border rounded-md p-1">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className={cn(
                    "h-7 w-7 p-0",
                    viewMode === 'grid' && "bg-primary text-primary-foreground"
                  )}
                  title="Grid View"
                  aria-label="Switch to grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'cards' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('cards')}
                  className={cn(
                    "h-7 w-7 p-0",
                    viewMode === 'cards' && "bg-primary text-primary-foreground"
                  )}
                  title="Card View"
                  aria-label="Switch to card view"
                >
                  <LayoutList className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonGrid count={6} />
          ) : filteredProjects.length === 0 ? (
            <EmptyState />
          ) : viewMode === 'grid' ? (
            <ActiveProjectsGrid 
              projects={filteredProjects} 
              onViewDetails={handleViewDetails}
            />
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredProjects.map((project) => (
                <EnhancedProjectCard
                  key={project.id}
                  project={project}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Project Details Dialog */}
      {selectedProject && (
        <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="font-mono">
                      {selectedProject.code}
                    </Badge>
                    <StatusBadge status={selectedProject.status} />
                    <PriorityBadge priority={selectedProject.priority} />
                  </div>
                  <DialogTitle className="text-2xl">{selectedProject.name}</DialogTitle>
                  <DialogDescription>{selectedProject.description}</DialogDescription>
                </div>
                <Button 
                  onClick={() => setShowProjectManagementModal(true)}
                  className="gap-2"
                >
                  <Eye className="h-4 w-4" />
                  View Project
                </Button>
              </div>
            </DialogHeader>

            <Tabs defaultValue="overview" className="mt-6">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="team">Team</TabsTrigger>
                <TabsTrigger value="timeline">Timeline</TabsTrigger>
                <TabsTrigger value="budget">Budget</TabsTrigger>
                <TabsTrigger value="time-entry">Time Entry</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-4 mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Building2 className="h-5 w-5" />
                      Client Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium">Client Name</p>
                        <p className="text-sm text-muted-foreground">{selectedProject.clientName}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Sector</p>
                        <Badge variant="secondary" className="mt-1">
                          {selectedProject.clientSector.replace('-', ' ')}
                        </Badge>
                      </div>
                      {selectedProject.clientContactName && (
                        <>
                          <div>
                            <p className="text-sm font-medium">Contact Person</p>
                            <p className="text-sm text-muted-foreground">{selectedProject.clientContactName}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium">Contact Email</p>
                            <p className="text-sm text-muted-foreground">{selectedProject.clientContactEmail}</p>
                          </div>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Target className="h-5 w-5" />
                      Project Objectives
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {selectedProject.objectives?.map((objective, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{objective}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      Key Deliverables
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {selectedProject.deliverables?.map((deliverable, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <div className="h-2 w-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                          <span className="text-sm">{deliverable}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                {selectedProject.specialInstructions && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Info className="h-5 w-5" />
                        Special Instructions
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">
                        {selectedProject.specialInstructions}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="team" className="space-y-4 mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Project Manager</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback>
                          {selectedProject.projectManager.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{selectedProject.projectManager}</p>
                        <p className="text-sm text-muted-foreground">{selectedProject.projectManagerEmail}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Team Members ({selectedProject.teamMembers.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[400px] pr-4">
                      <div className="space-y-4">
                        {selectedProject.teamMembers.map((member) => (
                          <div key={member.id} className="flex items-start justify-between border-b pb-4 last:border-0">
                            <div className="flex items-center gap-3">
                              <EmployeeImage 
                                name={member.name}
                                photoUrl={member.photoUrl}
                                size="md"
                              />
                              <div>
                                <p className="font-medium text-sm">{member.name}</p>
                                <p className="text-xs text-muted-foreground">{member.role}</p>
                                <p className="text-xs text-muted-foreground">{member.email}</p>
                              </div>
                            </div>
                            <Badge variant="secondary" className="text-xs">
                              {member.allocation}% allocated
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="timeline" className="space-y-4 mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Project Timeline</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium">Start Date</p>
                        <p className="text-sm text-muted-foreground">
                          {format(selectedProject.timeline.startDate, 'MMMM d, yyyy')}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">End Date</p>
                        <p className="text-sm text-muted-foreground">
                          {format(selectedProject.timeline.endDate, 'MMMM d, yyyy')}
                        </p>
                      </div>
                      {selectedProject.timeline.currentPhase && (
                        <div className="col-span-2">
                          <p className="text-sm font-medium">Current Phase</p>
                          <Badge variant="secondary" className="mt-1">
                            {selectedProject.timeline.currentPhase}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Milestones</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {selectedProject.timeline.milestones?.map((milestone) => (
                        <div key={milestone.id} className="flex items-start gap-3 border-l-2 border-muted pl-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm">{milestone.name}</p>
                              <Badge 
                                variant="outline" 
                                className={
                                  milestone.status === 'completed' 
                                    ? 'bg-green-500/10 text-green-700 dark:text-green-400'
                                    : milestone.status === 'in-progress'
                                    ? 'bg-blue-500/10 text-blue-700 dark:text-blue-400'
                                    : 'bg-gray-500/10 text-gray-700 dark:text-gray-400'
                                }
                              >
                                {milestone.status}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              Due: {format(milestone.dueDate, 'MMM d, yyyy')}
                            </p>
                            {milestone.description && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {milestone.description}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="budget" className="space-y-4 mt-4">
                {selectedProject.budget && (
                  <>
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Budget Overview</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <p className="text-sm font-medium">Total Hours</p>
                            <p className="text-2xl font-bold">{selectedProject.budget.totalHours.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium">Used Hours</p>
                            <p className="text-2xl font-bold">{selectedProject.budget.usedHours.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium">Remaining Hours</p>
                            <p className="text-2xl font-bold">{selectedProject.budget.remainingHours.toLocaleString()}</p>
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">Budget Utilization</span>
                            <span className="text-sm font-medium">
                              {Math.round((selectedProject.budget.usedHours / selectedProject.budget.totalHours) * 100)}%
                            </span>
                          </div>
                          <Progress 
                            value={(selectedProject.budget.usedHours / selectedProject.budget.totalHours) * 100}
                            className={cn(
                              "h-3",
                              (selectedProject.budget.usedHours / selectedProject.budget.totalHours) * 100 > 90 && "[&>div]:bg-red-500",
                              (selectedProject.budget.usedHours / selectedProject.budget.totalHours) * 100 > 75 && 
                              (selectedProject.budget.usedHours / selectedProject.budget.totalHours) * 100 <= 90 && "[&>div]:bg-amber-500"
                            )}
                          />
                          <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
                            <span>{selectedProject.budget.usedHours.toLocaleString()}h used</span>
                            <span>{selectedProject.budget.remainingHours.toLocaleString()}h remaining</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Scheduler Integration - Only show if scheduledHours exists */}
                    {selectedProject.budget.scheduledHours !== undefined && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <CalendarClock className="h-5 w-5" />
                            Scheduler Integration
                          </CardTitle>
                          <CardDescription>
                            Future scheduled hours from Resource Scheduler
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                          {/* Key Metrics Grid */}
                          <div className="grid grid-cols-3 gap-4">
                            <div className="p-4 border rounded-lg bg-blue-50 dark:bg-blue-950/20">
                              <p className="text-sm font-medium text-muted-foreground">Scheduled Hours</p>
                              <p className="text-3xl font-bold text-blue-700 dark:text-blue-400 mt-1">
                                {selectedProject.budget.scheduledHours.toLocaleString()}h
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Future allocated work
                              </p>
                            </div>
                            <div className="p-4 border rounded-lg bg-green-50 dark:bg-green-950/20">
                              <p className="text-sm font-medium text-muted-foreground">Available Capacity</p>
                              <p className="text-3xl font-bold text-green-700 dark:text-green-400 mt-1">
                                {Math.max(0, selectedProject.budget.remainingHours - selectedProject.budget.scheduledHours).toLocaleString()}h
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Unallocated hours
                              </p>
                            </div>
                            <div className="p-4 border rounded-lg bg-purple-50 dark:bg-purple-950/20">
                              <p className="text-sm font-medium text-muted-foreground">Capacity Used</p>
                              <p className="text-3xl font-bold text-purple-700 dark:text-purple-400 mt-1">
                                {Math.round((selectedProject.budget.scheduledHours / selectedProject.budget.remainingHours) * 100)}%
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Of remaining budget
                              </p>
                            </div>
                          </div>

                          {/* Scheduled Hours Progress Bar */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-1.5">
                                <CalendarClock className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm font-medium">Scheduled Hours</span>
                              </div>
                              <span className="text-sm font-medium">
                                {Math.round((selectedProject.budget.scheduledHours / selectedProject.budget.remainingHours) * 100)}%
                              </span>
                            </div>
                            <Progress 
                              value={(selectedProject.budget.scheduledHours / selectedProject.budget.remainingHours) * 100}
                              className="h-3 [&>div]:bg-blue-500"
                            />
                            <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
                              <span>{selectedProject.budget.scheduledHours.toLocaleString()}h scheduled</span>
                              <span>{Math.max(0, selectedProject.budget.remainingHours - selectedProject.budget.scheduledHours).toLocaleString()}h available</span>
                            </div>
                          </div>

                          {/* Health Status */}
                          <div className="flex items-center justify-center p-4 border rounded-lg bg-muted/30">
                            {(() => {
                              const scheduledPercentage = (selectedProject.budget.scheduledHours / selectedProject.budget.remainingHours) * 100;
                              if (scheduledPercentage < 60) {
                                return (
                                  <div className="flex items-center gap-3 text-amber-700 dark:text-amber-400">
                                    <TrendingDown className="h-6 w-6" />
                                    <div>
                                      <p className="font-semibold">Under-utilized</p>
                                      <p className="text-sm opacity-90">Capacity available for additional work</p>
                                    </div>
                                  </div>
                                );
                              } else if (scheduledPercentage <= 90) {
                                return (
                                  <div className="flex items-center gap-3 text-green-700 dark:text-green-400">
                                    <CheckCircle2 className="h-6 w-6" />
                                    <div>
                                      <p className="font-semibold">On Track</p>
                                      <p className="text-sm opacity-90">Well balanced resource allocation</p>
                                    </div>
                                  </div>
                                );
                              } else {
                                return (
                                  <div className="flex items-center gap-3 text-red-700 dark:text-red-400">
                                    <AlertCircle className="h-6 w-6" />
                                    <div>
                                      <p className="font-semibold">Over-scheduled</p>
                                      <p className="text-sm opacity-90">At capacity risk - review commitments</p>
                                    </div>
                                  </div>
                                );
                              }
                            })()}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {selectedProject.budget.totalCost && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Financial Budget</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <p className="text-sm font-medium">Total Budget</p>
                              <p className="text-2xl font-bold">
                                {formatCurrency(selectedProject.budget.totalCost)}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm font-medium">Used</p>
                              <p className="text-2xl font-bold">
                                {formatCurrency(selectedProject.budget.usedCost || 0)}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm font-medium">Remaining</p>
                              <p className="text-2xl font-bold">
                                {formatCurrency(selectedProject.budget.remainingCost || 0)}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}
              </TabsContent>

              <TabsContent value="time-entry" className="space-y-4 mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Time Entry Configuration</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <span className="text-sm font-medium">Requires Description</span>
                        <Badge variant={selectedProject.requiresDescription ? "default" : "secondary"}>
                          {selectedProject.requiresDescription ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <span className="text-sm font-medium">Requires Approval</span>
                        <Badge variant={selectedProject.requiresApproval ? "default" : "secondary"}>
                          {selectedProject.requiresApproval ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <span className="text-sm font-medium">Weekend Entry</span>
                        <Badge variant={selectedProject.allowWeekendEntry ? "default" : "secondary"}>
                          {selectedProject.allowWeekendEntry ? 'Allowed' : 'Not Allowed'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <span className="text-sm font-medium">Max Daily Hours</span>
                        <Badge variant="secondary">
                          {selectedProject.maxDailyHours || 'No limit'}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Available Time Entry Codes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {selectedProject.timeEntryCodes.map((code) => (
                        <div key={code.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className="font-mono">
                              {code.code}
                            </Badge>
                            <div>
                              <p className="text-sm font-medium">{code.name}</p>
                              {code.description && (
                                <p className="text-xs text-muted-foreground">{code.description}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {code.billable && (
                              <Badge variant="secondary" className="bg-green-500/10 text-green-700 dark:text-green-400">
                                Billable
                              </Badge>
                            )}
                            {code.requiresApproval && (
                              <Badge variant="secondary" className="bg-amber-500/10 text-amber-700 dark:text-amber-400">
                                Requires Approval
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      )}

      {/* Project Management Application Modal */}
      <Dialog open={showProjectManagementModal} onOpenChange={setShowProjectManagementModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Project Management Application
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center py-8">
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Eye className="h-8 w-8 text-primary" />
              </div>
              <p className="text-muted-foreground">
                This will open the Project in the Project Management Application.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowProjectManagementModal(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
